import { useEffect, useRef, useState } from "react";

export type CalendarView = "day" | "3day" | "week" | "month";

export type Route =
  | { page: "today" }
  /** `date` is any day inside the period shown, YYYY-MM-DD; null means today. */
  | { page: "calendar"; view: CalendarView | null; date: string | null }
  | { page: "projects" }
  | { page: "project"; id: string }
  | { page: "client"; id: string }
  | { page: "invoices" }
  | { page: "invoice"; id: string }
  | { page: "settings" }
  | { page: "trash" };

export function parseRoute(hash: string): Route {
  const [page, id, extra] = hash.replace(/^#\/?/, "").split("/");
  if (page === "calendar") {
    const view = id === "day" || id === "3day" || id === "week" || id === "month" ? id : null;
    return { page: "calendar", view, date: extra && /^\d{4}-\d{2}-\d{2}$/.test(extra) ? extra : null };
  }
  if (page === "projects") return { page: "projects" };
  if (page === "project" && id) return { page: "project", id: decodeURIComponent(id) };
  if (page === "client" && id) return { page: "client", id: decodeURIComponent(id) };
  if (page === "invoices") return { page: "invoices" };
  if (page === "invoice" && id) return { page: "invoice", id: decodeURIComponent(id) };
  if (page === "settings") return { page: "settings" };
  if (page === "trash") return { page: "trash" };
  return { page: "today" };
}

export function href(route: Route): string {
  switch (route.page) {
    case "project":
    case "client":
    case "invoice":
      return `#/${route.page}/${encodeURIComponent(route.id)}`;
    case "calendar":
      return route.view ? `#/calendar/${route.view}${route.date ? `/${route.date}` : ""}` : "#/calendar";
    default:
      return `#/${route.page}`;
  }
}

export function navigate(route: Route): void {
  window.location.hash = href(route);
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(window.location.hash));
  const current = useRef(route);
  useEffect(() => {
    const onChange = () => {
      const next = parseRoute(window.location.hash);
      // Paging through the Calendar keeps the page where it is.
      if (!(current.current.page === "calendar" && next.page === "calendar")) window.scrollTo({ top: 0 });
      current.current = next;
      setRoute(next);
    };
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}
